import React, { useState } from 'react';

import {
  AIRCRAFT_TYPE_OPTIONS_W_ALL,
  DEFAULT_OPTION,
  SELECT_OPTION_ALL,
} from 'constants';
import { AircraftFactory, ReportFactory } from 'classes';
import { getUTCShortDate, calculateMilliseconds } from 'utils';

import { Row, Col, Button } from 'react-bootstrap';
import Select from 'components/inputs/Select';
import ReportSummaryOverlay from 'components/overlays/ReportSummaryOverlay';

import styles from './styles.module.css';

const idPrefix = 'FlightReport';
const Flight = (props) => {
  const {
    aircraftMap,
    aircraftList,
    activeFlight,
    flightList,
    flightId,
    fleetFilter,
    onFleetFilterChange,
  } = props;

  if (flightId !== activeFlight?.id) {
    console.error('Flight Id is not same as active flight Id');
  }

  const flight = !flightId ? null : activeFlight;

  const [shipOption, setShipOption] = useState(
    !flight
      ? SELECT_OPTION_ALL.value
      : aircraftMap.get(flight?.aircraft_id).tail,
  );
  const [departureUTCOption, setDepartureUTCOption] = useState(
    !flight
      ? SELECT_OPTION_ALL.value
      : getUTCShortDate(flight.departure_timestamp),
  );
  const [overlayOpen, setOverlayOpen] = useState(!flight ? false : true);
  const [messageOverlayOpen, setMessageOverlayOpen] = useState(false);
  const [selectedFlightId, setSelectedFlightId] = useState(
    flight ? flight.id : SELECT_OPTION_ALL.value,
  );

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const dt30DaysAgo = todayDate.getTime() - calculateMilliseconds(30, 'days');

  const ship = [DEFAULT_OPTION].concat(
    !aircraftList
      ? []
      : (!fleetFilter
          ? aircraftList
          : aircraftList.filter(
              (aircraft) => aircraft?.aircraft_type?.id === fleetFilter,
            )
        )
          .map((aircraft) => {
            return {
              value: aircraft.tail,
              name: aircraft.tail,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
  );

  const filteredFlightList = !flightList
    ? []
    : flightList.reduce((arr, flight) => {
        const aircraft = AircraftFactory.getSummaryAircraftById(
          flight.aircraft_id,
        );
        if (
          (fleetFilter === SELECT_OPTION_ALL.value ||
            (aircraft && aircraft?.aircraft_type?.id === fleetFilter)) &&
          aircraft?.tail === shipOption &&
          (flight?.departure_timestamp > dt30DaysAgo ||
            !flight.departure_timestamp)
        ) {
          arr.push({
            id: flight.id,
            departure_timestamp: flight.departure_timestamp,
            utc: !flight.departure_timestamp
              ? 'NA'
              : getUTCShortDate(flight.departure_timestamp),
            number: flight.number,
            departure_airport: flight.departure_airport,
            destination_airport: flight.destination_airport,
          });
        }
        return arr;
      }, []);

  const departureUTC = [DEFAULT_OPTION].concat(
    filteredFlightList
      .reduce((arr, flight) => {
        const x = arr.find((item) => item.name === flight.utc);
        if (!x) {
          arr.push({
            dep: flight.departure_timestamp, // For sorting
            value: flight.utc,
            name: flight.utc,
          });
        }
        return arr;
      }, [])
      .sort((a, b) => b.dep - a.dep),
  );

  const flights = [DEFAULT_OPTION]
    .concat(
      filteredFlightList
        .filter((flight) => flight.utc === departureUTCOption)
        .map((flight) => {
          return {
            value: flight.id,
            name: `${
              !flight.number || flight.number === '--' ? 'NA' : flight.number
            }
          ${
            !flight.departure_airport.value.iata ||
            flight.departure_airport.value.iata === '---'
              ? 'NA'
              : flight.departure_airport.value.iata
          }-${
            !flight.destination_airport.value.iata ||
            flight.destination_airport.value.iata === '---'
              ? 'NA'
              : flight.destination_airport.value.iata
          }`,
          };
        }),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedFlight = !flightId
    ? filteredFlightList.find((fl) => fl.id === selectedFlightId)
    : flight;

  return (
    <>
      <Row className={styles.root} noGutters>
        <Col>
          <Row className={styles.title} noGutters>
            <Col data-cy-id={`${idPrefix}-ScreenHeader`} className="title_1">
              Flight report
            </Col>
            <Col md="auto" className={styles.note}>
              Note: Data is available for previous 7 days. For older flights use
              the Flights screen "Flight Reports" button.
            </Col>
          </Row>
          <Row
            className={styles.filterRow}
            data-cy-id={`${idPrefix}-Filters`}
            noGutters
          >
            <Col>
              <Row>
                <Col md="auto" className="mr-5">
                  <Select
                    idPrefix={`${idPrefix}-Fleet`}
                    data-cy-id={`${idPrefix}-FiltersPanel-FleetSelect`}
                    label="Fleet: "
                    options={AIRCRAFT_TYPE_OPTIONS_W_ALL}
                    value={fleetFilter}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      onFleetFilterChange(value);
                      setShipOption(SELECT_OPTION_ALL.value);
                      setDepartureUTCOption(SELECT_OPTION_ALL.value);
                      setSelectedFlightId(SELECT_OPTION_ALL.value);
                    }}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Ship`}
                    data-cy-id={`${idPrefix}-FiltersPanel-TailSelect`}
                    label="Tail: "
                    options={ship}
                    value={shipOption}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      setShipOption(value);
                      setDepartureUTCOption(SELECT_OPTION_ALL.value);
                      setSelectedFlightId(SELECT_OPTION_ALL.value);
                    }}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Departure-utc`}
                    data-cy-id={`${idPrefix}-FiltersPanel-DepartureDateSelect`}
                    label="Departure UTC Date: "
                    options={departureUTC}
                    value={departureUTCOption}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      setDepartureUTCOption(value);
                      setSelectedFlightId(SELECT_OPTION_ALL.value);
                    }}
                    disabled={departureUTC.length <= 1}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Flight`}
                    data-cy-id={`${idPrefix}-FiltersPanel-FlightSelect`}
                    label="Flight: "
                    options={flights}
                    value={selectedFlightId}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      setSelectedFlightId(value);
                    }}
                    disabled={flights.length <= 1}
                  />
                </Col>
              </Row>
            </Col>
            <Col md="auto">
              <Button
                className="button_foc"
                data-cy-id={`${idPrefix}-CreateReportButton`}
                disabled={!selectedFlightId}
                onClick={() => setOverlayOpen(true)}
              >
                Create report
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <ReportSummaryOverlay
        title="Flight report"
        data={
          <>
            <Row>
              <Col> Fleet </Col>
              <Col>
                {fleetFilter === SELECT_OPTION_ALL.value
                  ? SELECT_OPTION_ALL.name
                  : AIRCRAFT_TYPE_OPTIONS_W_ALL[fleetFilter]?.name}
              </Col>
            </Row>
            <Row>
              <Col> Tail </Col>
              <Col>
                {' '}
                {shipOption === SELECT_OPTION_ALL.value
                  ? SELECT_OPTION_ALL.name
                  : shipOption}{' '}
              </Col>
            </Row>
            <Row>
              <Col> Departure UTC </Col>
              <Col>
                {departureUTCOption === SELECT_OPTION_ALL.value
                  ? SELECT_OPTION_ALL.name
                  : departureUTCOption}
              </Col>
            </Row>
            <Row>
              <Col> Flight </Col>
              <Col>
                {selectedFlight
                  ? `${
                      !selectedFlight.number || selectedFlight.number === '--'
                        ? 'NA'
                        : selectedFlight.number
                    }
                  ${
                    !selectedFlight.departure_airport.value.iata ||
                    selectedFlight.departure_airport.value.iata === '---'
                      ? 'NA'
                      : selectedFlight.departure_airport.value.iata
                  }-${
                    !selectedFlight.destination_airport.value.iata ||
                    selectedFlight.destination_airport.value.iata === '---'
                      ? 'NA'
                      : selectedFlight.destination_airport.value.iata
                  }`
                  : null}
              </Col>
            </Row>
          </>
        }
        isOpen={overlayOpen}
        onCancel={() => setOverlayOpen(false)}
        loading={messageOverlayOpen}
        onDone={async () => {
          setMessageOverlayOpen(true);
          await ReportFactory.getFlightReport(selectedFlightId);
          setMessageOverlayOpen(false);
          setOverlayOpen(false);
        }}
      />
    </>
  );
};

export default Flight;
